<template>
  <widget class="post mx-auto post-card" :enable-card-size="enableCardSize">
    <component
      :is="disableNavigation ? 'div' : 'b-link'"
      :to="disableNavigation ? null : { name: 'post-details', params: { postKey: post.key } }"
      :class="{ 'post-card-link': !disableNavigation }"
      @click.native="handleCardClick"
    >
      <div>
        <!-- ** Header ** -->
        <div class="pt-1 w-100 d-flex align-items-center">
          <!-- Author avatar -->
          <safe-img
            class="logo"
            :src="createdBy.logo"
            :placeholder="createdBy.placeholder"
          />

          <!-- Author and date -->
          <div class="ml-1">
            <div class="d-flex">
              <p v-if="createdBy.name" class="font-weight-extrabold mb-0">
                {{ createdBy.name }}
              </p>
            </div>
            <small
              class="text-muted mb-0"
              :title="exactDate"
              @mouseover="hover = true"
              @mouseleave="hover = false"
            >
              {{ date }}
            </small>
          </div>

          <!-- Edit post button -->
          <b-button
            v-if="canEditPost"
            variant="link"
            class="ml-auto p-50"
            @click.stop="$emit('edit-post', post)"
          >
            <b-icon-pencil />
          </b-button>
        </div>
      </div>

      <!-- ** Text ** -->
      <div class="my-1 border-bottom">
        <append-preview :enabled="!!hasPreview && isContentReady">
          <div
            ref="content"
            class="text-break"
            :class="{ 'clip-content': isClippingContent }"
          >
            <content-with-anchors
              :content="post.content"
              @content-ready="isContentReady = true"
            />
          </div>
          <template #footer>
            <a
              v-if="isContentLong"
              class="d-block text-center text-muted font-weight-bolder mt-1"
              @click.stop="toggleShowMoreContent"
            >
              {{
                isClippingContent
                  ? $t("action.show-more")
                  : $t("action.show-less")
              }}
            </a>
          </template>
        </append-preview>

        <!-- ** Attachments ** -->
        <div v-if="hasAttachment" class="d-flex justify-content-center">
          <file-preview
            v-for="(file, index) in post.attachments"
            :key="index"
            :has-max-height="true"
            :src="attachmentURL[index]"
            :mime-type="post.attachments[index].mimeType"
          />
        </div>
      </div>

      <!-- ** Aditional content ** -->
      <div v-if="post.thumbnailURL" class="pb-2">
        <img class="w-50" :src="post.thumbnailURL">
      </div>

      <!-- ** Moderation ** -->
      <div @click.stop>
        <moderation-section
          :content="post"
          morph-type="posts"
          item-type="posts"
          item-type-name="post"
          :parent-key="post.key"
          :can-moderate="canModerate"
          @moderation-complete="handleModerationComplete"
        />
      </div>
      
      <!-- Moderation Feedback Modal -->
      <b-modal
        v-if="canModerate"
        v-model="isModerationFeedbackModalVisible"
        :title="$t('backoffice.moderation.feedback-modal.title')"
        centered
      >
        <b-form>
          <!-- AI Recommendation -->
          <div class="mb-2">
            <h6>{{ $t('backoffice.moderation.feedback-modal.ai-recommendation') }}</h6>
            <div class="d-flex align-items-center">
              <feather-icon
                :icon="moderationAction === 'accept' ? 'CheckCircleIcon' : 'AlertCircleIcon'"
                :class="moderationAction === 'accept' ? 'text-success' : 'text-danger'"
                size="18"
                class="mr-1"
              />
              <span :class="moderationAction === 'accept' ? 'text-success' : 'text-danger'">
                {{ $t(`backoffice.moderation.feedback-modal.${moderationAction}-recommendation`) }}
              </span>
            </div>
          </div>

          <!-- Feedback for the User -->
          <b-form-group
            :label="$t('backoffice.moderation.feedback-modal.user-feedback-label')"
          >
            <b-form-textarea
              v-model="moderationFeedback.feedbackForUser"
              rows="3"
              :placeholder="$t('backoffice.moderation.feedback-modal.user-feedback-placeholder')"
            />
          </b-form-group>

          <!-- Agreement with AI Recommendation -->
          <b-form-group class="mb-2">
            <b-form-radio-group
              v-model="moderationFeedback.agreesWithAI"
              :options="[
                { text: $t('backoffice.moderation.feedback-modal.agree-with-ai'), value: true },
                { text: $t('backoffice.moderation.feedback-modal.disagree-with-ai'), value: false }
              ]"
            />
          </b-form-group>

          <!-- Feedback for the AI agent (only shown when disagreeing) -->
          <b-form-group
            v-if="moderationFeedback.agreesWithAI === false"
            :label="$t('backoffice.moderation.feedback-modal.ai-feedback-label')"
          >
            <b-form-textarea
              v-model="moderationFeedback.aiFeedback"
              rows="3"
              :placeholder="$t('backoffice.moderation.feedback-modal.ai-feedback-placeholder')"
            />
          </b-form-group>
        </b-form>

        <template #modal-footer>
          <b-button
            variant="outline-secondary"
            @click="isModerationFeedbackModalVisible = false"
          >
            {{ $t('backoffice.moderation.feedback-modal.cancel') }}
          </b-button>
          <b-button
            :variant="moderationAction === 'accept' ? 'success' : 'danger'"
            @click="submitModeration"
          >
            {{ $t(`backoffice.moderation.feedback-modal.${moderationAction}`) }}
          </b-button>
        </template>
      </b-modal>

      <!-- ** Social buttons ** -->
      <div @click.stop>
        <social-buttons
          :comments-count="post.commentCount"
          :liked-by-member="post.likedByMember"
          :total-likes="post.totalLikes"
          :is-sending="isSending"
          :is-widget="isWidget"
          @change-like="toggleLike"
          @comment="toggleComments"
        />
      </div>

      <!-- ** Comment form ** -->
      <div v-if="showComments" @click.stop>
        <comments-section
          class="mt-2"
          :source-key="post.key"
          :can-moderate="canModerate"
          :comment-key-highlighted="commentKeyHighlighted"
          :sub-comment-key-highlighted="subCommentKeyHighlighted"
          morph-type="post"
          :leaf="false"
          @comment-created="handleCommentCreation"
          @comment-deleted="deleteComment"
        />
      </div>
    </component>
  </widget>
</template>

<script>
import { BButton, BIconPencil, BLink } from 'bootstrap-vue';
import { getImageResource } from '@/@core/utils/image-utils';
import ContentWithAnchors from '@core/components/string-utils/ContentWithAnchors.vue';
import Widget from '@core/widgets/Widget.vue';
import CardWidgetMixin from '@core/mixins/widgets/CardWidgetMixin';
import SafeImg from '@core/components/safe-img/SafeImg.vue';
import MembersPlaceholder from '@/assets/images/placeholders/light/members.svg';
import AvatarPlaceholder from '@/assets/images/placeholders/light/community-avatar.jpeg';
import SocialButtons from '@core/components/social/SocialButtons.vue';
import { CommentsSection } from '@/views/apps/comments/components';
import FilePreview from '@core/components/files/FilePreview.vue';
import { checkPermissions } from '@/@core/utils/roles-utils';
import AppendPreview from './AppendPreview.vue';
import ModerationSection from '@/@core/components/moderation/ModerationSection.vue';

export default {
  components: {
    BButton,
    BLink,
    Widget,
    SafeImg,
    FilePreview,
    AppendPreview,
    ContentWithAnchors,
    SocialButtons,
    CommentsSection,
    BIconPencil,
    ModerationSection,
  },
  mixins: [CardWidgetMixin],
  props: {
    post: {
      type: Object,
      required: true,
    },
    isWidget: {
      type: Boolean,
      default: false,
    },
    // FIXME: This prop should be removed when post is removed from copernicData,
    // so that all lists of posts use the same logic
    disableStore: Boolean,
    disableNavigation: {
      type: Boolean,
      default: false,
    },
    commentsCollapsed: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loading: false,
      isClippingContent: true,
      isContentLong: false,
      showComments: !this.commentsCollapsed,
      isContentReady: false,
      hover: false,
      isSending: false,
      // Moderation
      isModerationFeedbackModalVisible: false,
      moderationAction: null,
      selectedPostForModeration: null,
      moderationFeedback: {
        feedbackForUser: '',
        aiFeedback: '',
        agreesWithAI: null
      },
    };
  },
  computed: {
    locale() {
      return this.$store.state.locale.currentLocale;
    },
    loggedMember() {
      return this.$store.getters.loggedMember;
    },
    exactDate() {
      const date = new Date(this.post.createdAt);
      return date.toLocaleString();
    },
    date() {
      return this.$moment(this.post.createdAt).locale(this.locale).fromNow();
    },
    hasPreview() {
      return (
        this.post.showPreview && (this.post.attachments || []).length === 0
      );
    },
    hasAttachment() {
      return !this.post.showPreview && this.post.attachments?.length > 0;
    },
    attachmentURL() {
      if (!this.post.attachments?.length) {
        return '';
      }

      const url = [];

      for (const row of this.post.attachments) {
        url.push(row.filePath);
      }

      return url;
    },
    canEditPost() {
      return (
        this.post.createdByMember?.key === this.loggedMember?.key
        || this.$store.getters.currentCollective.isStaff
        || checkPermissions(
          'update',
          'post',
          this.loggedMemberRoles,
          this.$store.getters.currentCollective,
        )
      );
    },
    isStaff() {
      return this.$store.getters.currentCollective.isStaff;
    },
    canManagePosts() {
      return checkPermissions(
          'manage',   
          'post',
          this.loggedMemberRoles,
          this.$store.getters.currentCollective,
        )
    },
    canModerate() {
      return this.isStaff || this.canManagePosts;
    },
    createdBy() {
      if (this.post.createdByMember) {
        return {
          name: `${this.post.createdByMember.name} ${this.post.createdByMember.surname}`,
          logo: getImageResource(this.post.createdByMember.avatar_url),
          placeholder: MembersPlaceholder,
        };
      }

      if (this.post.createdByCollective) {
        return {
          name: this.post.createdByCollective.name,
          logo: getImageResource(this.post.createdByCollective.logoURL),
          placeholder: AvatarPlaceholder,
        };
      }

      return { placeholder: MembersPlaceholder };
    },
    commentKeyHighlighted() {
      // in url it can come something like this:
      // https://nexos.bancolombia.com/pruebas-nectios/posts/9efb15d0-9e21-11f0-bb03-d99d5bcf0efa?commentKey=58d4a0d0-9fa7-11f0-a58b-4f9f9c73507b
      // we need to get the commentKey from the url
      const url = new URL(window.location.href);
      const commentKey = url.searchParams.get('commentKey');
      return commentKey;
    },
    subCommentKeyHighlighted() {
      // Handle nested comment highlighting from URL parameters
      // https://nexos.bancolombia.com/pruebas-nectios/posts/9efb15d0-9e21-11f0-bb03-d99d5bcf0efa?commentKey=f08b43f0-9fae-11f0-bcb8-8f596cf6800d&subCommentKey=bc42de70-a2b3-11f0-830c-6138cb4bcbbc
      const url = new URL(window.location.href);
      const subCommentKey = url.searchParams.get('subCommentKey');
      return subCommentKey;
    },
  },
  async mounted() {
    const { scrollHeight, offsetHeight } = this.$refs.content;
    this.isContentLong = this.$route.name === 'post-details' ? false : scrollHeight > offsetHeight;
    this.isClippingContent = this.isContentLong;
  },
  methods: {
    handleCardClick(event) {
      // Only navigate if the click wasn't on an interactive element
      // This is handled by @click.stop on interactive elements
      return true;
    },
    deleteComment() {
      --this.post.commentCount;
    },
    toggleShowMoreContent() {
      this.isClippingContent = !this.isClippingContent;
    },
    async toggleComments() {
      this.showComments = !this.showComments;
      if (this.showComments) {
        await this.$nextTick();
        this.$refs.commentForm?.focus();
      }
    },
    async toggleLike() {
      console.log('toggleLike in posts');
      if (this.disableStore) {
        // TODO: This should be replaced when implemented the proper post store.
        this.$emit('toggle-like', this.post);
      } else {
        this.isSending = true;
        this.$socket.emit('updateData', {
          communitySlug: this.$store.getters.currentCollective.slug,
          itemType: 'posts',
          key: this.post.key,
          data: { totalLikes: this.post.likedByMember === true ? '--' : '++' },
        });

        await this.$store.dispatch('toggleLike', {
          itemType: 'posts',
          morphType: 'post',
          key: this.post.key,
          item: this.post,
        });
        this.isSending = false;
      }
    },
    handleCommentCreation() {
      this.$socket.emit('updateData', {
        communitySlug: this.$store.getters.currentCollective.slug,
        itemType: 'posts',
        key: this.post.key,
        data: { commentCount: '++' },
      });
    },
    async handleModerationComplete({ action, contentKey, response }) {
      if (response) {
        console.log('handleModerationComplete response', response);
        Object.assign(this.post, response);
      }
      
      this.$emit('moderation-complete', {
        action: action,
        contentKey: contentKey
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.logo {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}
.post {
  // this position fixes a bug with the card and the scroll library.
  // if not present the post disapears as we scroll down.
  position: inherit;
}

.post-card-link {
  color: inherit;
  text-decoration: none;
  
  &:hover {
    color: inherit;
    text-decoration: none;
  }
  
  // Add hover effect to the entire card
  &:hover .post {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
}

.clip-content {
  position: relative;
  max-height: 200px;
  overflow: hidden;

  &:after {
    content: "";
    position: absolute;
    z-index: 1;
    bottom: 0;
    left: 0;
    pointer-events: none;
    background-image: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, 1) 90%
    );
    width: 100%;
    height: 3em;
  }
}
</style>
